import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { db } from "../firebase"; // ✅ make sure this path matches your project
import { collection, getDocs, query, limit } from "firebase/firestore";

const CoursePage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Fetch limited featured courses
  const fetchCourses = async () => {
    try {
      const q = query(collection(db, "courses"));
      const snapshot = await getDocs(q);
  
      // Map Firestore data
      const courseList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
  
      // ✅ Sort: featured first, then by latest createdAt
      const sortedCourses = courseList.sort((a, b) => {
        // 1️⃣ Featured first
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
  
        // 2️⃣ Within same group, latest createdAt first
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime;
      });
  
      // ✅ Take top 3
      setCourses(sortedCourses.slice(0, 3));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching featured courses:", error);
      setLoading(false);
    }
  };
  

  useEffect(() => {
    AOS.init({ duration: 1000 });
    fetchCourses();
  }, []);

  return (
    <div className="bg-white w-screen max-w-none font-sans relative overflow-visible pt-24 md:pt-32">
      {/* YouTube Video Section */}
      <div
        className="relative flex justify-center items-center z-30 -mt-32 md:-mt-40"
        data-aos="fade-up"
      >
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden w-[90%] md:w-[70%] lg:w-[55%]">
          <div className="aspect-video w-full">
          <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/ZGxF7GexXyU?si=CFBs8tFz3GZWVwBW"
            title="Course Introduction Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="rounded-2xl"
          ></iframe>

          </div>
        </div>
      </div>

      {/* Featured Courses Section */}
      <section
        className="text-center py-12 px-4 relative z-10"
        data-aos="fade-up"
      >
        <p className="text-orange-500 uppercase text-sm tracking-wider font-semibold mb-2">
          Featured Courses
        </p>
        <h1 className="font-bricolage text-3xl sm:text-4xl md:text-5xl font-bold text-black leading-snug sm:leading-tight">
          Courses Designed for Your <br/>Success
        </h1>
      </section>

      {/* Course Cards Section */}
      <section className="relative px-6 md:px-16 pb-16">
        {/* Left Arrow - Hidden on Mobile */}
        <button className="hidden md:flex absolute left-2 top-1/2 transform -translate-y-1/2 bg-white border shadow-md rounded-full w-10 h-10 items-center justify-center z-10">
          &lt;
        </button>

        {/* Course Cards Grid */}
        {loading ? (
          <p className="text-center text-gray-600">Loading featured courses...</p>
        ) : courses.length === 0 ? (
          <p className="text-center text-gray-600">No courses available.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
            {courses.map((course) => (
              <div
                key={course.id}
                data-aos="zoom-in"
                className="relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow"
              >
                {/* ⭐ Featured Badge */}
                {course.featured && (
                  <div className="absolute top-3 right-3  bg-black text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md flex items-center gap-1">
                    <span>⭐</span> Featured
                  </div>
                )}

                {course.imageUrl && (
                  <img
                    src={course.imageUrl}
                    alt={course.title}
                    className="w-full h-auto object-cover"
                  />
                )}

                <div className="p-4">
                  <h2 className="text-lg font-semibold mb-2 text-gray-800">
                    {course.title}
                  </h2>
                  <p className="text-gray-600 text-sm mb-4">
                    {course.description}
                  </p>
                  <p className="text-lg font-semibold text-gray-900 mb-4">
                    ₹ {course.price}
                  </p>

                  {/* ✅ Show View / Enroll button */}
                  {course.courseLink ? (
                    <a
                      href={(() => {
                        let link = course.courseLink.trim();
                        link = link.replace(/^http\/\/:/i, "http://").replace(/^https\/\/:/i, "https://");
                        if (!/^https?:\/\//i.test(link)) link = `https://${link}`;
                        return link;
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-[#F15A29] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#e14f1f] transition inline-block"
                    >
                      View Course
                    </a>
                  ) : (
                    <button className="bg-[#F15A29] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#e14f1f] transition">
                      Enroll now
                    </button>
                  )}
                </div>
              </div>

            ))}
          </div>
        )}

        {/* Right Arrow - Hidden on Mobile */}
        <button className="hidden md:flex absolute right-2 top-1/2 transform -translate-y-1/2 bg-white border shadow-md rounded-full w-10 h-10 items-center justify-center z-10">
          &gt;
        </button>

        {/* View All Courses Button */}
        <div className="mt-10 text-center relative z-10" data-aos="fade-up">
          <a
            href="/courses"
            className="bg-orange-500 text-white px-6 py-3 rounded-full text-lg font-semibold hover:bg-orange-700 transition"
          >
            View All Courses
          </a>
        </div>
      </section>
    </div>
  );
};

export default CoursePage;
